<template>
    <div>
        <div class="card card-gray mt-3">
            <div class="card-body">
                <div class="row">
                    <div class="col-3">
                        <label class="mb-1 form-label">
                            {{ $t('accessLands.landNumber') }}
                        </label>
                        <div class="input-group">
                            <input type="number" min="1" class="form-control" v-model.number="prefixNumber">
                            <div class="px-3 py-2">/</div>
                            <input type="number" min="1" class="form-control" v-model.number="suffixNumber">
                        </div>
                    </div>
                    <div class="col-3">
                        <label class="mb-1 form-label">
                            {{ $t('accessLands.landType') }}
                        </label>
                        <select class="form-select" v-model="landType">
                            <option v-for="landType in landTypes" :value="landType">
                                {{ $t('accessLands.landTypes.' + landType) }}
                            </option>
                        </select>
                    </div>
                    <div class="col-5">
                        <label class="mb-1 form-label">
                            {{ $t('accessLands.description') }}
                        </label>
                        <input type="text" class="form-control" v-model="description">
                    </div>
                    <div class="col-1 text-end align-content-end">
                        <button @click="removeLandAccess(value)"
                                class="btn btn-outline-secondary mt-auto"
                                :title="$t('form.buttonRemove')">
                            <span class="fas fa-times"></span>
                        </button>
                    </div>
                </div>
            </div>
            <div v-if="error" class="invalid-feedback d-block px-3">{{ error }}</div>
        </div>
    </div>
</template>

<script lang="ts">
import {AccessLand} from '@/components/estimates/common/controls/values';
import {computed, defineComponent, onMounted, PropType, ref, watch} from 'vue';
import {TranslateResult, useI18n} from 'vue-i18n';

export default defineComponent({
    props: {
        landTypes: {
            type: Array as PropType<string[]>,
            required: true,
        },
        value: {
            type: Object as PropType<AccessLand | null>,
            default: null,
        },
        validate: {
            type: Boolean,
            required: true,
        },
    },
    emits: ['input', 'remove'],
    setup(props, {emit}) {
        const {t} = useI18n();

        const prefixNumber = ref<number | null>(null);
        const suffixNumber = ref<number | null>(null);
        const landType = ref('');
        const description = ref<string | null>(null);

        const newValue = computed((): AccessLand | null => {
            if (prefixNumber.value === null || landType.value === '') {
                return null;
            }

            return {
                prefixNumber: prefixNumber.value,
                suffixNumber: suffixNumber.value,
                landType: landType.value,
                description: description.value,
            };
        });

        function getError(): TranslateResult | null {
            if (prefixNumber.value === null) {
                return t('form.errors.accessLands.prefixNumberNotFilled');
            }

            if (landType.value === '') {
                return t('form.errors.accessLands.landTypeNotSelected');
            }

            return null;
        }

        const error = computed((): TranslateResult | null => {
            return props.validate
                ? getError()
                : null;
        });

        function removeLandAccess(): void {
            emit('remove');
        }

        onMounted(() => {
            if (props.value !== null) {
                prefixNumber.value = props.value.prefixNumber;
                suffixNumber.value = props.value.suffixNumber;
                landType.value = props.value.landType;
                description.value = props.value.description;
            }
        });

        watch(
            [prefixNumber, suffixNumber, landType, description],
            () => {
                emit('input', newValue.value);
            },
        );

        return {
            prefixNumber,
            suffixNumber,
            landType,
            description,
            error,
            removeLandAccess,
        };
    },
});
</script>
